import { useRef, useState } from "react";
import { UploadCloud, FileCheck2, Loader2, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const MAX = 10 * 1024 * 1024;
const OK = ["application/pdf", "image/png", "image/jpeg"];

export function FileDropzone({ onExtracted }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const handle = async (file) => {
    if (!file) return;
    if (!OK.includes(file.type) && !/\.(pdf|png|jpe?g)$/i.test(file.name)) {
      return toast.error("Only PDF, PNG, or JPEG files are supported.");
    }
    if (file.size > MAX) return toast.error("File exceeds the 10MB limit.");
    setBusy(true);
    setDone(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/syllabus/extract", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDone({ name: data.filename, chars: data.chars });
      onExtracted?.(data.text, data.filename);
      toast.success(`Extracted ${data.chars.toLocaleString()} characters from ${data.filename}.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div
        data-testid="file-dropzone"
        onClick={() => !busy && inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); handle(e.dataTransfer.files?.[0]); }}
        className={`cursor-pointer rounded-xl border-2 border-dashed p-5 text-center transition-colors ${
          drag ? "border-persian-blue bg-persian-blue/10" : "border-white/25 bg-white/5 hover:border-persian-blue/60"
        }`}
      >
        {busy ? (
          <div className="flex flex-col items-center gap-2 text-slate-200">
            <Loader2 className="h-6 w-6 animate-spin text-persian-blue" />
            <span className="text-sm">Reading your document…</span>
          </div>
        ) : done ? (
          <div className="flex items-center justify-center gap-2 text-slate-100">
            <FileCheck2 className="h-5 w-5 text-persian-blue" />
            <span className="text-sm truncate max-w-[220px]">{done.name}</span>
            <button data-testid="file-clear" onClick={(e) => { e.stopPropagation(); setDone(null); }}
              className="text-slate-400 hover:text-white"><X className="h-4 w-4" /></button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1.5 text-slate-300">
            <UploadCloud className="h-7 w-7 text-persian-blue" />
            <span className="text-sm font-medium text-white">Drag &amp; drop a syllabus</span>
            <span className="text-xs">PDF, PNG or JPEG · up to 10MB</span>
          </div>
        )}
        <input ref={inputRef} type="file" accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
          hidden data-testid="file-input" onChange={(e) => handle(e.target.files?.[0])} />
      </div>
    </div>
  );
}
